import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

export interface QueueMetrics {
  totalQueues: number
  queued: number
  started: number
  finished: number
  crashed: number
  rejected: number
  cancelled: number
}

export interface ActiveQueue {
  id: number
  uuid: string
  project_id: number
  project_name: string
  organization_id: number
  organization_name: string
  motive: string
  status: string
  created_at: string
  updated_at: string
  started_at?: string
  duration_seconds?: number
  changes_count?: number
  created_by?: number
}

export interface QueueProcessingStats {
  byProject: Array<{
    project_id: number
    project_name: string
    organization_name: string
    queues_processed: number
    total_movements: number
    avg_duration_seconds: number
    last_processed: string
  }>
  byOrganization: Array<{
    organization_id: number
    organization_name: string
    queues_processed: number
    total_movements: number
    avg_duration_seconds: number
    projects_count: number
  }>
  byMotive: Array<{
    motive: string
    count: number
    avg_duration_seconds: number
  }>
}

export interface QueueMonitoringData {
  metrics: QueueMetrics
  activeQueues: ActiveQueue[]
  processingStats: QueueProcessingStats
  recentActivity: Array<{
    queue_id: number
    uuid: string
    project_name: string
    organization_name: string
    motive: string
    status: string
    status_changed_at: string
    duration_seconds?: number
  }>
}

export const queueMonitoringApi = createApi({
  reducerPath: 'queueMonitoringApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin/queue-monitoring',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['QueueMonitoring'],
  endpoints: (builder) => ({
    getQueueMonitoringData: builder.query<QueueMonitoringData, void>({
      query: () => '/data',
      transformResponse: (response: any) => response.data,
      providesTags: ['QueueMonitoring'],
    }),
    getQueueMetrics: builder.query<QueueMetrics, void>({
      query: () => '/metrics',
      transformResponse: (response: any) => response.data,
      providesTags: ['QueueMonitoring'],
    }),
    getActiveQueues: builder.query<ActiveQueue[], void>({
      query: () => '/active',
      transformResponse: (response: any) => response.data,
      providesTags: ['QueueMonitoring'],
    }),
    getProcessingStats: builder.query<QueueProcessingStats, void>({
      query: () => '/stats',
      transformResponse: (response: any) => response.data,
      providesTags: ['QueueMonitoring'],
    }),
    cancelQueue: builder.mutation<{ success: boolean; message: string }, number>({
      query: (queueId) => ({
        url: `/${queueId}/cancel`,
        method: 'POST',
      }),
      transformResponse: (response: any) => response,
      invalidatesTags: ['QueueMonitoring'],
    }),
  }),
})

export const {
  useGetQueueMonitoringDataQuery,
  useGetQueueMetricsQuery,
  useGetActiveQueuesQuery,
  useGetProcessingStatsQuery,
  useCancelQueueMutation,
} = queueMonitoringApi
